import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCapabilities } from '../../components/admin/CapabilityProvider';
import { getRoles, createRole } from '../../services/rbacService.js';
import NotAuthorized from '../../components/admin/NotAuthorized.jsx';
import RoleCreateModal from '../../components/admin/RoleCreateModal.jsx';
import ResultBanner from '../../components/admin/ResultBanner.jsx';
import AdminPageHeader from '../../components/admin/ui/AdminPageHeader';
import AdminTable, { AdminTableHeader, AdminTableRow, AdminTableCell, AdminTableHeaderCell } from '../../components/admin/ui/AdminTable';
import AdminButton from '../../components/admin/ui/AdminButton';
import AdminEmptyState from '../../components/admin/ui/AdminEmptyState';

export default function RolesListPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [resultBanner, setResultBanner] = useState(null);
  const { has } = useCapabilities();
  const navigate = useNavigate();

  const canReadRbac = has('admin:rbac:read');
  const canCreateRole = has('admin:roles:create');

  useEffect(() => {
    if (!canReadRbac) {
      setLoading(false);
      return;
    }
    loadRoles();
  }, [canReadRbac]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getRoles();
      setRoles(response.roles || []);
    } catch (err) {
      setError(err.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (data) => {
    try {
      setCreateLoading(true);
      setCreateError(null);
      await createRole(data.name, data.description, data.capabilities, data.reason);
      setShowCreateModal(false);
      setResultBanner({
        type: 'success',
        message: `Role "${data.name}" created successfully`,
      });
      // Refresh roles list
      await loadRoles();
    } catch (err) {
      setCreateError(err.message || 'Failed to create role');
    } finally {
      setCreateLoading(false);
    }
  };

  if (!canReadRbac) {
    return <NotAuthorized requiredCapability="admin:rbac:read" />;
  }

  if (loading) {
    return <div className="admin-loading">Loading roles...</div>;
  }

  if (error) {
    return (
      <div role="alert" aria-live="polite">
        <p>Error: {error}</p>
        <AdminButton onClick={loadRoles} ariaLabel="Retry loading roles">
          Retry
        </AdminButton>
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader
        title="Roles"
        rightActions={
          canCreateRole ? (
            <AdminButton
              variant="primary"
              onClick={() => {
                setShowCreateModal(true);
                setCreateError(null);
              }}
              ariaLabel="Create new role"
            >
              Create Role
            </AdminButton>
          ) : null
        }
      />

      {resultBanner && (
        <ResultBanner
          type={resultBanner.type}
          message={resultBanner.message}
          onClose={() => setResultBanner(null)}
        />
      )}

      {roles.length === 0 ? (
        <AdminEmptyState
          title="No roles found"
          description="There are no roles in the system yet."
        />
      ) : (
        <AdminTable>
          <AdminTableHeader>
            <AdminTableHeaderCell>Name</AdminTableHeaderCell>
            <AdminTableHeaderCell>Display Name</AdminTableHeaderCell>
            <AdminTableHeaderCell>Capabilities</AdminTableHeaderCell>
            <AdminTableHeaderCell>Actions</AdminTableHeaderCell>
          </AdminTableHeader>
          <tbody>
            {roles.map((role) => (
              <AdminTableRow key={role.id}>
                <AdminTableCell>
                  <code style={{ fontFamily: 'var(--font-family-monospace)', fontSize: 'var(--font-size-sm)' }}>{role.name}</code>
                </AdminTableCell>
                <AdminTableCell>{role.displayName}</AdminTableCell>
                <AdminTableCell>
                  {role.capabilityIds?.length > 0 ? (
                    <span>{role.capabilityIds.length} capability{role.capabilityIds.length !== 1 ? 'ies' : ''}</span>
                  ) : (
                    <span className="admin-text-tertiary" style={{ fontStyle: 'italic' }}>No capabilities</span>
                  )}
                </AdminTableCell>
                <AdminTableCell>
                  <AdminButton
                    variant="ghost"
                    onClick={() => navigate(`/admin/roles/${role.id}`)}
                    ariaLabel={`View details for role ${role.displayName}`}
                  >
                    View
                  </AdminButton>
                </AdminTableCell>
              </AdminTableRow>
            ))}
          </tbody>
        </AdminTable>
      )}

      <RoleCreateModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateError(null);
        }}
        onConfirm={handleCreateRole}
        loading={createLoading}
        error={createError}
      />
    </div>
  );
}

// Styles removed - now using CSS classes from admin-ui.css

